import React, { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { CurrencyProvider } from "@/context/CurrencyContext";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import CartDrawer from "@/components/layout/CartDrawer";
import NewsletterPopup from "@/components/layout/NewsletterPopup";
import WhatsAppButton from "@/components/layout/WhatsAppButton";
import SearchOverlay from "@/components/layout/SearchOverlay";

import Home from "@/pages/Home";
import Collection from "@/pages/Collection";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import Account from "@/pages/Account";
import Wishlist from "@/pages/Wishlist";
import About from "@/pages/About";
import { BlogList, BlogPost } from "@/pages/Blog";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminDiscounts from "@/pages/admin/AdminDiscounts";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminReminders from "@/pages/admin/AdminReminders";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminImport from "@/pages/admin/AdminImport";

function StorefrontShell({ children }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const hideChrome = location.pathname.startsWith("/admin") || location.pathname === "/login" || location.pathname === "/signup";

  return (
    <>
      {!hideChrome && <Header onOpenSearch={() => setSearchOpen(true)} />}
      {children}
      {!hideChrome && <Footer />}
      {!hideChrome && <CartDrawer />}
      {!hideChrome && <NewsletterPopup />}
      {!hideChrome && <WhatsAppButton />}
      {!hideChrome && <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />}
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  // Handle OAuth callback (fragment-based) at the app root — synchronous during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <StorefrontShell>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/collections/:key" element={<Collection />} />
        <Route path="/shop/:key" element={<Collection />} />
        <Route path="/craft/:key" element={<Collection />} />
        <Route path="/product/:slug" element={<ProductDetail />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/wishlist" element={<Wishlist />} />
        <Route path="/account" element={<Account />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="reviews" element={<AdminReviews />} />
          <Route path="reminders" element={<AdminReminders />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="discounts" element={<AdminDiscounts />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="newsletter" element={<AdminNewsletter />} />
          <Route path="import" element={<AdminImport />} />
        </Route>
      </Routes>
    </StorefrontShell>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <CurrencyProvider>
          <AuthProvider>
            <CartProvider>
              <AppRouter />
              <Toaster
                position="top-center"
                toastOptions={{
                  style: {
                    background: "#FDFBF7",
                    color: "#1A1A1A",
                    border: "1px solid #BCA37F",
                    borderRadius: 0,
                    fontFamily: "Outfit, sans-serif",
                  },
                }}
              />
            </CartProvider>
          </AuthProvider>
        </CurrencyProvider>
      </BrowserRouter>
    </div>
  );
}
